// BankAccountsTab.jsx — the accounts a brand bills FROM, kept as a named list.
//
// The same shape as the Clients tab and for the same reason. A client used to
// be free text typed onto each invoice, so the same client spelled two ways was
// two clients; bank details were free text typed onto each invoice, so the same
// account was retyped from memory every time and a digit wrong in a routing
// number is an invoice the client cannot pay.
//
// `BrandSettings.invoicePayment` still holds ONE standing account per brand and
// still fills the gaps. This is the catalogue for brands that bill from more
// than one — a USD ACH account and a EUR IBAN are two accounts, not one setting
// with an argument about which value belongs in it.
//
// NAME AND METHOD ARE WHAT AN ACCOUNT IS PICKED BY. The bank's name is not
// enough: the same bank holds the ACH account and the wire account, and a
// dropdown showing "Chase" twice is a dropdown you cannot use.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  listBankAccounts, createBankAccount, updateBankAccount, deleteBankAccount,
  getBrandAddress, setBrandAddress,
} from "../../api/invoices/invoices";

// How a transfer reaches the account. Prints as the block heading on the
// invoice — "Payment Information (Wire)" — and is half of how an account is
// recognised in the picker. A fixed list, because it is a label a client reads
// and "ach"/"ACH "/"A.C.H." are three spellings of one thing.
export const METHODS = ['ACH', 'Wire', 'SEPA', 'SWIFT', 'Faster Payments', 'Other'];
export const ACCOUNT_TYPES = ['Checking', 'Savings', 'Business Checking', 'Business Savings'];

// The filter's own sentinel, kept out of the id space so a brand can never
// collide with it. Same rule as ClientsTab's.
export const NO_BRAND = '__none__';

export function brandLabel(brands, brandId) {
  if (!brandId) return '';
  const b = (brands || []).find((x) => String(x._id || x.id) === String(brandId));
  // An account under a brand the caller cannot see is still a real filing.
  return b ? (b.name || '') : `unknown brand (${String(brandId).slice(-6)})`;
}

export function filterAccountsByBrand(accounts, brandFilter) {
  if (!brandFilter) return accounts || [];
  if (brandFilter === NO_BRAND) return (accounts || []).filter((a) => !a.brand_id);
  return (accounts || []).filter((a) => String(a.brand_id || '') === String(brandFilter));
}

/**
 * How an account reads in a dropdown, and in the list.
 *
 * Name first, because that is what the operator chose it by; then the method,
 * because the same bank holds more than one kind of account; then the account
 * type, when there is one, since Checking and Savings at the same bank under
 * the same name is exactly the pair a person picks wrong.
 *
 * Exported and pure so the picker in the invoice form and the row in this tab
 * cannot describe the same account two different ways.
 */
export function accountLabel(a) {
  if (!a) return '';
  const bits = [String(a.method || '').trim(), String(a.accountType || '').trim()].filter(Boolean);
  return `${String(a.name || '').trim() || 'Untitled account'}${bits.length ? ` — ${bits.join(' · ')}` : ''}`;
}

// The last four digits and nothing else. A full account number has no business
// sitting in a list on screen — it is on the invoice, where it belongs, and the
// operator opening the row can see all of it.
export function maskAccount(value) {
  const v = String(value || '').replace(/\s+/g, '');
  if (!v) return '';
  return v.length <= 4 ? `••${v}` : `••••${v.slice(-4)}`;
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 620, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', borderRadius: 8, padding: 22 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3">
          <h5 className="mb-0">{title}</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────── the account form
function BankAccountModal({ brands, account, onClose, onSaved }) {
  const editing = !!(account && account._id);
  const [name, setName] = useState(account?.name || "");
  const [brandId, setBrandId] = useState(account?.brand_id || "");
  const [method, setMethod] = useState(account?.method || "ACH");
  const [bankName, setBankName] = useState(account?.bankName || "");
  const [accountHolderName, setAccountHolderName] = useState(account?.accountHolderName || "");
  const [accountType, setAccountType] = useState(account?.accountType || "");
  const [routingNumber, setRoutingNumber] = useState(account?.routingNumber || "");
  const [accountNumber, setAccountNumber] = useState(account?.accountNumber || "");
  // SWIFT, IBAN, a sort code, a reference to quote. Free label + value, because
  // the set differs by country and a fixed list would be wrong somewhere.
  const [extraLines, setExtraLines] = useState(
    (account?.extraLines || []).map((l) => ({ label: l.label || '', value: l.value || '' })),
  );
  const [isDefault, setIsDefault] = useState(!!account?.isDefault);
  const [notes, setNotes] = useState(account?.notes || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const setLine = (i, key, v) => setExtraLines((rows) => rows.map((r, n) => (n === i ? { ...r, [key]: v } : r)));
  const addLine = () => setExtraLines((rows) => [...rows, { label: '', value: '' }]);
  const dropLine = (i) => setExtraLines((rows) => rows.filter((_, n) => n !== i));

  const submit = async () => {
    setError("");
    if (!name.trim()) { setError("Give this account a name — it is what you pick it by."); return; }
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        brand_id: brandId || null,
        method, bankName, accountHolderName, accountType,
        routingNumber: routingNumber.trim(),
        accountNumber: accountNumber.trim(),
        extraLines: extraLines.filter((l) => l.label.trim() || l.value.trim()),
        isDefault,
        notes,
      };
      if (editing) await updateBankAccount(account._id, body);
      else await createBankAccount(body);
      onSaved();
    } catch (e) { setError((e && e.message) || "Could not save the account"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={editing ? `Edit ${account.name}` : "New bank account"} onClose={onClose}>
      {error && <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div>}

      <label style={{ fontSize: 12, color: '#666' }}>Name *</label>
      <input
        className="form-control mb-1"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Chase — Operating"
      />
      <div style={{ fontSize: 11, color: '#8a949c', marginBottom: 10 }}>
        What you will pick it by on an invoice. Not the bank’s name — the same
        bank can hold more than one of these.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label style={{ fontSize: 12, color: '#666' }}>Brand — whose invoices offer it
          <select className="form-control" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            <option value="">Company-wide (every brand)</option>
            {(brands || []).map((b) => (<option key={b._id || b.id} value={b._id || b.id}>{b.name}</option>))}
          </select>
        </label>
        <label style={{ fontSize: 12, color: '#666' }}>Method
          <select className="form-control" value={method} onChange={(e) => setMethod(e.target.value)}>
            {/* An older account with a method typed before this list existed
                keeps showing it rather than silently becoming ACH. */}
            {!METHODS.includes(method) && method ? <option value={method}>{method}</option> : null}
            {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <span style={{ fontSize: 11, color: '#8a949c' }}>Prints as the heading: “Payment Information ({method})”.</span>
        </label>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
        <label style={{ fontSize: 12, color: '#666' }}>Bank name
          <input className="form-control" value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. Chase" />
        </label>
        <label style={{ fontSize: 12, color: '#666' }}>Account holder name
          <input className="form-control" value={accountHolderName} onChange={(e) => setAccountHolderName(e.target.value)} placeholder="the name on the account" />
        </label>
        <label style={{ fontSize: 12, color: '#666' }}>Routing number
          <input className="form-control" value={routingNumber} onChange={(e) => setRoutingNumber(e.target.value)} placeholder="9 digits for ACH" />
        </label>
        <label style={{ fontSize: 12, color: '#666' }}>Account number
          <input className="form-control" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
        </label>
        {/* Which KIND of account. A bank needs it and the holder's name does
            not give it to them. Left blank it simply does not print. */}
        <label style={{ fontSize: 12, color: '#666' }}>Account type
          <select className="form-control" value={accountType} onChange={(e) => setAccountType(e.target.value)}>
            <option value="">Not stated</option>
            {!ACCOUNT_TYPES.includes(accountType) && accountType ? <option value={accountType}>{accountType}</option> : null}
            {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
      </div>

      {/* Everything the fixed fields above do not carry. A EUR account is an
          IBAN and a SWIFT code and nothing else; a UK one is a sort code. */}
      <div style={{ marginTop: 12 }}>
        <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>
          Extra lines <span style={{ color: '#8a949c' }}>— IBAN, SWIFT, a sort code, a reference to quote</span>
        </label>
        {extraLines.map((l, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
            <input className="form-control" style={{ flex: '0 0 34%' }} placeholder="Label — e.g. IBAN" value={l.label} onChange={(e) => setLine(i, 'label', e.target.value)} />
            <input className="form-control" style={{ flex: 1 }} placeholder="Value" value={l.value} onChange={(e) => setLine(i, 'value', e.target.value)} />
            <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => dropLine(i)}>×</button>
          </div>
        ))}
        <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={addLine}>+ Add line</button>
      </div>

      <label style={{ fontSize: 12, color: '#666', display: 'block', marginTop: 12 }}>Notes <span style={{ color: '#8a949c' }}>— for you, never printed</span>
        <input className="form-control" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>

      {/* At most one per brand, and the server demotes the others rather than
          asking the operator to go and untick the old one. */}
      <label style={{ fontSize: 12, color: '#666', display: 'block', marginTop: 12 }}>
        <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />{' '}
        Offer this one first on new invoices for {brandId ? (brandLabel(brands, brandId) || 'this brand') : 'every brand'}
      </label>

      <div className="alert alert-info" style={{ fontSize: 12, padding: '6px 10px', marginTop: 12 }}>
        Picking this account on an invoice <strong>copies</strong> these details onto it.
        Editing them here changes what future invoices are filled with — it never
        rewrites an invoice a client has already been sent.
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy}>{busy ? "Saving…" : "Save account"}</button>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────── the tab
// THE BRAND'S "FROM" ADDRESS (the user, 2026-10-15). Every invoice a brand
// compiles prints it under "From" unless the invoice carries its own; it is
// also the postal line in the brand's email footers. It sat in brand settings
// with no screen to edit it, so the only way to find a wrong one was a PDF.
export function BrandFromAddress({ brands, canEdit }) {
  const [brandId, setBrandId] = useState('');
  const [address, setAddress] = useState('');
  const [saved, setSaved] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!brandId && (brands || []).length) setBrandId(String(brands[0]._id || brands[0].id));
  }, [brands, brandId]);

  useEffect(() => {
    setAddress(''); setSaved(''); setErr(''); setNote('');
    if (!brandId) return undefined;
    let live = true;
    (async () => {
      try {
        const r = await getBrandAddress(brandId);
        const a = (r && r.data && r.data.mailingAddress) || '';
        if (live) { setAddress(a); setSaved(a); }
      } catch (e) { if (live) setErr((e && (e.error || e.message)) || 'Could not load the address'); }
    })();
    return () => { live = false; };
  }, [brandId]);

  const save = async () => {
    setBusy(true); setErr(''); setNote('');
    try {
      const r = await setBrandAddress(brandId, address);
      const a = (r && r.data && r.data.mailingAddress) || '';
      setAddress(a); setSaved(a);
      setNote('Saved. Invoices compiled from now on print it under "From"; already-sent PDFs are unchanged until they are saved again.');
    } catch (e) { setErr((e && (e.error || e.message)) || 'Could not save the address'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ border: '1px solid #eee', borderRadius: 6, padding: 12, marginBottom: 16 }}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>Invoice "From" address</div>
      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr auto', gap: 8, alignItems: 'start' }}>
        <select className="form-control" aria-label="From address brand" value={brandId} onChange={(e) => setBrandId(e.target.value)} style={{ fontSize: 13 }}>
          {(brands || []).map((b) => (<option key={b._id || b.id} value={b._id || b.id}>{b.name}</option>))}
        </select>
        <textarea
          className="form-control" aria-label="Brand mailing address" rows={2} style={{ fontSize: 13 }}
          value={address} onChange={(e) => setAddress(e.target.value)} disabled={!canEdit || !brandId}
          placeholder="Street, suite, city, state ZIP — prints as one line"
        />
        {canEdit && (
          <button className="acc-btn acc-btn-primary" onClick={save} disabled={busy || !brandId || address === saved}>
            {busy ? 'Saving…' : 'Save address'}
          </button>
        )}
      </div>
      <div style={{ fontSize: 11, color: '#8a949c', marginTop: 4 }}>
        Printed under "From" on this brand's invoices, and as the postal address in its email footers.
      </div>
      {err && <div className="alert alert-warning" style={{ fontSize: 13, marginTop: 6 }}>{err}</div>}
      {note && <div className="alert alert-success" style={{ fontSize: 13, marginTop: 6 }}>{note}</div>}
    </div>
  );
}

export default function BankAccountsTab({ can, brands }) {
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null);   // {} for new, account for edit
  const [brandFilter, setBrandFilter] = useState('');

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try { const r = await listBankAccounts(); setAccounts((r && r.data) || []); }
    catch (e) { setErr((e && e.message) || "Failed to load bank accounts"); setAccounts([]); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => filterAccountsByBrand(accounts, brandFilter), [accounts, brandFilter]);
  const unbranded = useMemo(() => accounts.filter((a) => !a.brand_id).length, [accounts]);

  const remove = async (a) => {
    if (!window.confirm(
      `Delete ${a.name}?\n\nInvoices already filled from it keep their own copy of the details — `
      + 'nothing a client has been sent changes.'
    )) return;
    try { await deleteBankAccount(a._id); await load(); }
    catch (e) { setErr((e && e.message) || "Could not delete the account"); }
  };

  const canEdit = can('INVOICE_EDIT');

  return (
    <div>
      <style>{`
        .ba-row { border-bottom: 1px solid #f1f1f1; padding: 10px 4px; }
        .ba-name { font-weight: 600; font-size: 14px; }
        .ba-meta { font-size: 12px; color: #777; }
        .ba-num { font-size: 12px; color: #999; font-family: monospace; }
        .ba-brand { background: #eef2f6; color: #3c4a54; border-radius: 10px; padding: 1px 8px; font-size: 11px; }
        .ba-nobrand { background: #fdf3e3; color: #8a5a00; }
        .ba-default { background: #e7f5ec; color: #1e6b3e; border-radius: 10px; padding: 1px 8px; font-size: 11px; }
      `}</style>

      <BrandFromAddress brands={brands} canEdit={canEdit} />

      <div className="d-flex align-items-center justify-content-between mb-2" style={{ gap: 10 }}>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <label style={{ fontSize: 12, color: '#666', margin: 0 }}>Brand</label>
          <select
            className="form-control"
            style={{ width: 260, fontSize: 13 }}
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
          >
            <option value="">All brands ({accounts.length})</option>
            <option value={NO_BRAND}>Company-wide — every brand ({unbranded})</option>
            {(brands || []).map((b) => {
              const id = String(b._id || b.id);
              const n = accounts.filter((a) => String(a.brand_id || '') === id).length;
              return <option key={id} value={id}>{b.name} ({n})</option>;
            })}
          </select>
          {brandFilter && (
            <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setBrandFilter('')}>Clear</button>
          )}
        </div>
        {canEdit && <button className="acc-btn acc-btn-primary" onClick={() => setModal({})}>+ New bank account</button>}
      </div>

      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      {busy && accounts.length === 0 && <div className="cm-empty">Loading…</div>}
      {!busy && accounts.length === 0 && (
        <div className="cm-empty">
          No bank accounts yet. Add one and it becomes selectable by name when you create an invoice —
          instead of typing a routing number onto every invoice from memory.
        </div>
      )}
      {!busy && accounts.length > 0 && shown.length === 0 && (
        <div className="cm-empty">No accounts under that brand.</div>
      )}

      {shown.map((a) => (
        <div className="ba-row" key={a._id}>
          <div className="d-flex align-items-baseline justify-content-between" style={{ gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <span className="ba-name">{a.name}</span>
              {a.method ? <span className="ba-meta"> · {a.method}</span> : null}
              {a.accountType ? <span className="ba-meta"> · {a.accountType}</span> : null}
              {a.isDefault && <span className="ba-default" style={{ marginLeft: 6 }}>default</span>}
              <span
                className={`ba-brand ${a.brand_id ? '' : 'ba-nobrand'}`}
                style={{ marginLeft: 6 }}
                title={a.brand_id ? 'Offered on this brand’s invoices' : 'Offered on every brand’s invoices'}
              >
                {brandLabel(brands, a.brand_id) || 'company-wide'}
              </span>
              <div className="ba-meta">
                {[a.bankName, a.accountHolderName].filter(Boolean).join(' · ') || <span style={{ color: '#bbb' }}>no bank or holder set</span>}
              </div>
              {/* The last four and no more. The full number belongs on the
                  invoice, not in a list anyone walking past a screen can read. */}
              <div className="ba-num">
                {a.routingNumber ? `routing ${maskAccount(a.routingNumber)}` : ''}
                {a.routingNumber && a.accountNumber ? '  ·  ' : ''}
                {a.accountNumber ? `account ${maskAccount(a.accountNumber)}` : ''}
                {(a.extraLines || []).length ? `  ·  ${(a.extraLines || []).map((l) => l.label).filter(Boolean).join(', ')}` : ''}
              </div>
              {a.notes ? <div className="ba-meta" style={{ fontStyle: 'italic' }}>{a.notes}</div> : null}
            </div>
            {canEdit && (
              <div style={{ whiteSpace: 'nowrap' }}>
                <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setModal(a)}>Edit</button>
                <button className="acc-btn ms-1" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => remove(a)}>Delete</button>
              </div>
            )}
          </div>
        </div>
      ))}

      {modal && (
        <BankAccountModal
          key={modal._id || 'new'}
          brands={brands}
          account={modal._id ? modal : null}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
